import type { ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { CircleCheck, Mail, Package } from "lucide-react";
import { Card, CardContent } from "@baluu/ui/components/card";
import { CloudinaryUrl__TransformedUrl } from "@obby/lib";
import type { ViewContext } from "../types";
import { BookingApiError, bookingJson } from "../api";
import { BookingLoading, BookingProblem, RecordNotFound } from "../parts";
import { isOtherSchool } from "../school";
import { formatMoney } from "../money";
import { expiryDate, giftAddress, giftMessage, giftTimeline, type GiftCard, type TimelineStep } from "./success/giftcardReceipt";

// /giftcard/receipt/<gift card>: the receipt after buying a gift card.
// Port of v1's giftcard/receipt/[giftCardId] (page + GiftCardReceiptView):
// what happens next (the buyer's confirmation, the recipient's email, the
// post) beside the card itself (its cover and amount, who it's for, when
// it expires, where it's posted, the email, the message). The gift card's
// id is what lets the customer in, as on v1 (GET
// /widget/pages/payment/giftcard/sucess/:id, no sign-in). v1's
// /giftcard/payment/success?giftCardId= only redirects here (the website
// app does that redirect).

const ICONS: Record<TimelineStep["icon"], typeof CircleCheck> = { sent: CircleCheck, mail: Mail, post: Package };

export function GiftcardReceiptView({ ctx, giftCardId }: { ctx: ViewContext; giftCardId: string }) {
  const page = useQuery({
    queryKey: ["booking", "giftcard-receipt", ctx.api, giftCardId],
    queryFn: () => bookingJson<{ giftcard?: GiftCard | null }>(`${ctx.api}/widget/pages/payment/giftcard/sucess/${encodeURIComponent(giftCardId)}`),
    retry: (count, error) => count < 2 && !(error instanceof BookingApiError),
  });

  if (page.isPending) return <BookingLoading rows={2} />;
  if (page.isError) {
    const notFound = page.error instanceof BookingApiError && page.error.status === 404;
    return notFound ? (
      <RecordNotFound what="gift card" />
    ) : (
      <BookingProblem title="Your gift card receipt couldn’t be loaded" message="Please try again in a moment." onRetry={() => void page.refetch()} />
    );
  }
  const card = page.data?.giftcard;
  // v1 rendered an empty receipt for a missing gift card.
  if (!card) return <RecordNotFound what="gift card" />;
  if (isOtherSchool(ctx.businessId, card.school, card.widgetBoughtSchoolID)) return <RecordNotFound what="gift card" />;
  return <Receipt card={card} />;
}

function Receipt({ card }: { card: GiftCard }) {
  const cover =
    card.details?.cover && /^https?:\/\//.test(card.details.cover)
      ? (CloudinaryUrl__TransformedUrl(card.details.cover, { width: 560, height: 374, crop: "fill" }) as string)
      : undefined;
  const value = formatMoney(card.amount, card.currencyUsed);
  const expires = expiryDate(card.expiresAt);
  const address = giftAddress(card);
  const message = giftMessage(card);

  return (
    <div className="grid gap-8 md:grid-cols-2">
      <section className="space-y-4">
        <span className="text-sm font-medium uppercase tracking-widest text-muted-foreground">Confirmation</span>
        <h1 className="text-3xl font-semibold uppercase leading-tight tracking-tight">You’ve given the gift of learning!</h1>
        <p className="text-muted-foreground">
          You’ve given someone one of the best gifts they could receive, the power to learn something new! Check the timeline below to see what
          happens next.
        </p>
        <ul className="space-y-4">
          {giftTimeline(card).map((step) => {
            const Icon = ICONS[step.icon];
            return (
              <li key={step.icon} className="flex gap-3">
                <Icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
                <div>
                  <p className="font-medium uppercase">{step.title}</p>
                  <p className="text-sm text-muted-foreground">{step.text}</p>
                </div>
              </li>
            );
          })}
        </ul>
      </section>

      <Card className="overflow-hidden border-border/60 shadow-lg">
        {cover && (
          <div className="relative">
            {/* eslint-disable-next-line @next/next/no-img-element -- a web component, not a Next page */}
            <img src={cover} alt="Gift card" className="block w-full object-cover" />
            <span className="absolute bottom-4 right-4 rounded-full bg-background/90 px-4 py-2 text-lg font-bold">{value}</span>
          </div>
        )}
        <CardContent className="space-y-4 p-6">
          <div className="flex flex-wrap gap-4">
            {card.recipient && <Field label="To">{card.recipient}</Field>}
            <Field label="Amount">{value}</Field>
            {expires && <Field label="Expires">{expires}</Field>}
          </div>
          {address && <Field label="Address">{address}</Field>}
          {card.email && <Field label="Email">{card.email}</Field>}
          {message && <Field label="Message">{message}</Field>}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0 flex-1">
      <p className="text-sm uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="break-words font-medium">{children}</p>
    </div>
  );
}
